/**
 * Site Contact field formatter
 */

import {useSiteContact} from '@hconnect/tuc-processes/fields'
import {useChangeableField} from '@hconnect/tuc-processes/orderManagement/orderChange'

import {phoneNumberToString} from '../../../orderIntake/utils/phoneNumberUtils'

import type {FieldFormatter, FieldFormatterResult} from './types'

const buildContactValues = (
  oldName: string | null,
  newName: string | null,
  oldPhone: string | null,
  newPhone: string | null,
  hasChanges: boolean,
  notSpecifiedText: string
): FieldFormatterResult['values'] => {
  const nameChanged = oldName !== newName
  const phoneChanged = oldPhone !== newPhone

  const values: Array<{text: string; isDiscarded: boolean; isNew: boolean}> = []

  // Add name values
  if (oldName && nameChanged) {
    values.push({text: oldName, isDiscarded: true, isNew: false})
  }
  if (newName) {
    values.push({text: newName, isDiscarded: false, isNew: nameChanged})
  }

  // Add phone values (with separator if name exists)
  if (oldPhone && phoneChanged) {
    const phoneText = newName || oldName ? ` - ${oldPhone}` : oldPhone
    values.push({text: phoneText, isDiscarded: true, isNew: false})
  }
  if (newPhone) {
    const phoneText = newName ? ` - ${newPhone}` : newPhone
    values.push({text: phoneText, isDiscarded: false, isNew: phoneChanged})
  }

  // If no values, show "Not specified"
  if (values.length === 0) {
    values.push({text: notSpecifiedText, isDiscarded: false, isNew: false})
  }

  return values
}

export const useSiteContactFormatter = (): FieldFormatter => {
  const {name, phoneNumber, defaultName, defaultPhoneNumber} = useSiteContact()
  const siteContactState = useChangeableField('siteContact')

  return (context) => {
    const {t} = context
    const requiresApproval = siteContactState.state === 'requireDispatchConfirmation'
    const hasChanges = siteContactState.isDirty

    // Format name and phone separately so they can be marked independently
    const formattedOldName = defaultName?.trim() || null
    const formattedOldPhone =
      defaultPhoneNumber && defaultPhoneNumber.dialingCode !== undefined
        ? phoneNumberToString(
            defaultPhoneNumber as {
              dialingCode: string | null
              number: string
              country: string
            }
          )
        : null

    const formattedNewName = name?.trim() || null
    const formattedNewPhone = phoneNumber ? phoneNumberToString(phoneNumber) : null

    const values = buildContactValues(
      formattedOldName,
      formattedNewName,
      formattedOldPhone,
      formattedNewPhone,
      hasChanges,
      t('orderIntake.rmc.orderManagement.notSpecified', 'Not specified')
    )

    return {values, requiresApproval, hasChanges}
  }
}
